import { ColumnDefinition } from '@approvalmax/ui/src/components/Table/Table.types';
import { compareHelpers, errorHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { createSelector } from 'reselect';

import { getDelegateByUser } from '../../helpers/helpItems';
import type { UserRow } from './UserList.types';
import { ColumnId, Comparator } from './UserList.types';

export const getSortFunction = (
    column: ColumnDefinition<UserRow>,
    companyTeam: UserRow[],
    company: selectors.types.ExpandedCompany
): Comparator<UserRow, number> => {
    let nameComparator: typeof compareHelpers.stringComparator2AscI;
    let emailComparator: typeof compareHelpers.stringComparator2AscI;

    if (column.sortOrder === 'asc' || column.id !== ColumnId.Name) {
        nameComparator = compareHelpers.stringComparator2AscI;
        emailComparator = compareHelpers.stringComparator2AscI;
    } else {
        nameComparator = compareHelpers.stringComparator2DescI;
        emailComparator = compareHelpers.stringComparator2DescI;
    }

    const resultingComparator: Comparator = (a, b) => {
        const result = nameComparator(a.displayName, b.displayName);

        return result === 0 ? emailComparator(a.userEmail, b.userEmail) : result;
    };

    switch (column.id) {
        case ColumnId.Name.toString():
            return resultingComparator;

        case ColumnId.Email.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const result = comparator(a.userEmail, b.userEmail);

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.Role.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const result = comparator(
                    selectors.company.getCompanyUserRoleText(a.role, a.id === company.author),
                    selectors.company.getCompanyUserRoleText(b.role, b.id === company.author)
                );

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.Status.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const result = comparator(a.statusText, b.statusText);

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.TFA.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.booleanComparator2Asc
                    : compareHelpers.booleanComparator2Desc;

            return (a, b) => {
                const result = comparator(a.isTFAEnabled, b.isTFAEnabled);

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.Delegate.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const delegateA = companyTeam.find((x) => x.id === a.delegateUserId);
                const delegateB = companyTeam.find((x) => x.id === b.delegateUserId);
                const result = comparator(
                    delegateA ? delegateA.displayName : '',
                    delegateB ? delegateB.displayName : ''
                );

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.DelegateDateStart.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const delegateA = getDelegateByUser(company, a);
                const delegateB = getDelegateByUser(company, b);

                const result = comparator(
                    delegateA ? delegateA.delegateFrom : '',
                    delegateB ? delegateB.delegateFrom : ''
                );

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        case ColumnId.DelegateDateEnd.toString(): {
            const comparator =
                column.sortOrder === 'asc'
                    ? compareHelpers.stringComparator2AscI
                    : compareHelpers.stringComparator2DescI;

            return (a, b) => {
                const delegateA = getDelegateByUser(company, a);
                const delegateB = getDelegateByUser(company, b);

                const result = comparator(delegateA ? delegateA.delegateTo : '', delegateB ? delegateB.delegateTo : '');

                return result === 0 ? resultingComparator(a, b) : result;
            };
        }

        default:
            throw errorHelpers.invalidOperationError();
    }
};

export const getFilterFunction = (filterText: string) => {
    if (!filterText) {
        return () => true;
    }

    const filterNormalized = filterText.toLowerCase();

    return (user: UserRow) =>
        user.displayName.toLowerCase().includes(filterNormalized) ||
        user.userEmail.toLowerCase().includes(filterNormalized);
};

export const getListData: (
    state: Record<string, unknown> | null,
    companyTeam: selectors.types.ExpandedCompanyUser[],
    filterText: string,
    company: selectors.types.ExpandedCompany,
    colDef?: ColumnDefinition<UserRow>
) => UserRow[] = createSelector(
    (
        state: null,
        companyTeam: UserRow[],
        _filterText: string,
        _company: selectors.types.ExpandedCompany,
        _colDef?: ColumnDefinition<UserRow>
    ) => companyTeam,
    (
        state: null,
        companyTeam: UserRow[],
        filterText: string,
        company: selectors.types.ExpandedCompany,
        colDef?: ColumnDefinition<UserRow>
    ) => colDef,
    (
        state: null,
        companyTeam: UserRow[],
        filterText: string,
        _company: selectors.types.ExpandedCompany,
        _colDef?: ColumnDefinition<UserRow>
    ) => filterText,
    (
        state: null,
        companyTeam: UserRow[],
        filterText: string,
        company: selectors.types.ExpandedCompany,
        _colDef?: ColumnDefinition<UserRow>
    ) => company,
    (companyTeam, colDef, filterText, company) => {
        return colDef
            ? companyTeam.filter(getFilterFunction(filterText)).sort(getSortFunction(colDef, companyTeam, company))
            : companyTeam.filter(getFilterFunction(filterText));
    }
);
